import React, { useState, useMemo } from 'react';
import { Download, Calendar, TrendingUp, DollarSign, Users, Package, BarChart3, PieChart, FileText, Filter } from 'lucide-react';
import { exportToCSV, exportToXLSX } from '../lib/exporters';
import { useData } from '../providers/DataProvider';
import { computeSaleChargeBreakdown } from '../lib/orderCharges';

const Reports = ({ transactions = [] }) => {
  const [dateRange, setDateRange] = useState('month');
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [reportType, setReportType] = useState('general');
  
  const data = useData();
  const { branches = [], clients = [], suppliers = [], brokers = [], transactionProfits = [], branchProfits = [], profitCenters = [] } = data || {};
  const monipaySummary = useMemo(() => {
    const centers = Array.isArray(profitCenters) ? profitCenters : [];
    return centers.find((pc) => String(pc.code || '').toUpperCase() === 'MONIPAY') || null;
  }, [profitCenters]);

  
  // Calcular fechas según el rango seleccionado
  const getDateRange = () => {
    const end = new Date();
    const start = new Date();
    
    switch(dateRange) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        break;
      case 'week':
        start.setDate(start.getDate() - 7);
        break;
      case 'month':
        start.setMonth(start.getMonth() - 1);
        break;
      case 'quarter':
        start.setMonth(start.getMonth() - 3);
        break;
      case 'year':
        start.setFullYear(start.getFullYear() - 1);
        break;
      default:
        start.setMonth(start.getMonth() - 1);
    }
    
    return { start, end };
  };
  
  const { start: startDate, end: endDate } = getDateRange();
  
  // Filtrar transacciones por fecha y sucursal
  const filteredTransactions = useMemo(() => {
    return transactions.filter(t => {
      const transDate = new Date(t.date || t.created_at);
      const inDateRange = transDate >= startDate && transDate <= endDate;
      const inBranch = selectedBranch === 'all' || String(t.branchId || t.branch_id) === selectedBranch;
      const notCancelled = t.status !== 'cancelled';
      return inDateRange && inBranch && notCancelled;
    });
  }, [transactions, startDate, endDate, selectedBranch]);
  
  // Filtrar profits por fecha
  const filteredProfits = useMemo(() => {
    return transactionProfits.filter(tp => {
      const trans = filteredTransactions.find(t => t.id === tp.transaction_id);
      return trans !== undefined;
    });
  }, [transactionProfits, filteredTransactions]);
  
  // Métricas principales
  const metrics = useMemo(() => {
    const sales = filteredTransactions.filter(t => (t.type || t.transaction_type) === 'sale');
    const orders = filteredTransactions.filter(t => (t.type || t.transaction_type) === 'order');
    
    // Usar datos de profits si están disponibles
    let totalRevenue = 0;
    let totalCosts = 0;
    let totalProfit = 0;
    
    if (filteredProfits.length > 0) {
      totalRevenue = filteredProfits.reduce((sum, p) => sum + Number(p.total_revenue || 0), 0);
      totalCosts = filteredProfits.reduce((sum, p) => sum + Number(p.total_costs || 0), 0);
      totalProfit = filteredProfits.reduce((sum, p) => sum + Number(p.net_profit || 0), 0);
    } else {
      // Fallback a cálculos antiguos
      totalRevenue = sales.reduce((sum, s) => sum + Number(s.amount || 0), 0);
      totalProfit = sales.reduce((sum, s) => {
        const breakdown = computeSaleChargeBreakdown(s);
        return sum + (breakdown.internal || 0);
      }, 0);
      totalCosts = orders.reduce((sum, o) => sum + Number(o.amount || 0), 0);
    }
    
    return {
      totalSales: sales.length,
      totalOrders: orders.length,
      totalRevenue,
      totalCosts,
      totalProfit,
      averageTicket: sales.length > 0 ? totalRevenue / sales.length : 0,
      profitMargin: totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0,
      monipayBalance: Number(monipaySummary?.current_balance || monipaySummary?.net_profit || 0) || 0,
    };
  }, [filteredTransactions, filteredProfits, monipaySummary]);
  
  // Datos por sucursal
  const branchData = useMemo(() => {
    return branches.map(branch => {
      const branchTrans = filteredTransactions.filter(t => 
        String(t.branchId || t.branch_id) === String(branch.id)
      );
      
      const branchProfitData = branchProfits.find(bp => 
        String(bp.branch_id) === String(branch.id)
      );
      
      const sales = branchTrans.filter(t => (t.type || t.transaction_type) === 'sale');
      const orders = branchTrans.filter(t => (t.type || t.transaction_type) === 'order');
      
      return {
        id: branch.id,
        name: branch.name,
        salesCount: sales.length,
        ordersCount: orders.length,
        revenue: sales.reduce((sum, s) => sum + Number(s.amount || 0), 0),
        profit: branchProfitData?.net_profit || 0,
        balance: branchProfitData?.current_balance || 0,
      };
    });
  }, [branches, filteredTransactions, branchProfits]);
  
  // Top clientes
  const topClients = useMemo(() => {
    const clientStats = new Map();
    
    filteredTransactions
      .filter(t => (t.type || t.transaction_type) === 'sale' && t.clientId)
      .forEach(t => {
        const clientId = String(t.clientId || t.client_id);
        const client = clients.find(c => String(c.id) === clientId);
        if (!client) return;
        
        if (!clientStats.has(clientId)) {
          clientStats.set(clientId, {
            id: clientId,
            name: client.name || client.company_name || 'Sin nombre',
            salesCount: 0,
            totalAmount: 0,
            profit: 0,
          });
        }
        
        const stats = clientStats.get(clientId);
        stats.salesCount += 1;
        stats.totalAmount += Number(t.amount || 0);
        
        const breakdown = computeSaleChargeBreakdown(t);
        stats.profit += breakdown.internal || 0;
      });
    
    return Array.from(clientStats.values())
      .sort((a, b) => b.totalAmount - a.totalAmount)
      .slice(0, 10);
  }, [filteredTransactions, clients]);
  
  // Top proveedores
  const topSuppliers = useMemo(() => {
    const supplierStats = new Map();
    
    filteredTransactions
      .filter(t => (t.type || t.transaction_type) === 'order' && t.supplierId)
      .forEach(t => {
        const supplierId = String(t.supplierId || t.supplier_id);
        const supplier = suppliers.find(s => String(s.id) === supplierId);
        if (!supplier) return;
        
        if (!supplierStats.has(supplierId)) {
          supplierStats.set(supplierId, {
            id: supplierId,
            name: supplier.name || 'Sin nombre',
            ordersCount: 0,
            totalAmount: 0,
          });
        }
        
        const stats = supplierStats.get(supplierId);
        stats.ordersCount += 1;
        stats.totalAmount += Number(t.amount || 0);
      });
    
    return Array.from(supplierStats.values())
      .sort((a, b) => b.totalAmount - a.totalAmount)
      .slice(0, 10);
  }, [filteredTransactions, suppliers]);
  
  // Función para exportar datos
  const exportCSV = () => {
    const rows = filteredTransactions.map((t) => ({
      id: t.id,
      fecha: t.date || t.created_at,
      tipo: t.type || t.transaction_type,
      cliente: clients.find(c => String(c.id) === String(t.clientId || t.client_id))?.name || '',
      proveedor: suppliers.find(s => String(s.id) === String(t.supplierId || t.supplier_id))?.name || '',
      sucursal: branches.find(b => String(b.id) === String(t.branchId || t.branch_id))?.name || '',
      estado: t.status,
      monto: Number(t.amount || 0),
    }));
    exportToCSV(`reporte_${dateRange}_${new Date().toISOString().slice(0,10)}`, rows);
  };
  const exportXLSX = async () => {
    const txRows = filteredTransactions.map((t) => ({
      id: t.id,
      fecha: t.date || t.created_at,
      tipo: t.type || t.transaction_type,
      cliente: clients.find(c => String(c.id) === String(t.clientId || t.client_id))?.name || '',
      proveedor: suppliers.find(s => String(s.id) === String(t.supplierId || t.supplier_id))?.name || '',
      sucursal: branches.find(b => String(b.id) === String(t.branchId || t.branch_id))?.name || '',
      estado: t.status,
      monto: Number(t.amount || 0),
    }));
    const metricsRow = [{
      periodo: `${startDate.toLocaleDateString('es-ES')} - ${endDate.toLocaleDateString('es-ES')}`,
      ventas: metrics.totalSales,
      pedidos: metrics.totalOrders,
      ingresos: metrics.totalRevenue,
      costos: metrics.totalCosts,
      ganancia: metrics.totalProfit,
      margen_pct: Number(metrics.profitMargin?.toFixed?.(2) || metrics.profitMargin),
      ticket_promedio: metrics.averageTicket,
    }];
    const profitsRows = (transactionProfits || []).filter(tp => filteredTransactions.some(t => t.id === tp.transaction_id)).map(tp => ({
      transaccion_id: tp.transaction_id,
      tipo: tp.transaction_type,
      ingresos: Number(tp.total_revenue || 0),
      costos: Number(tp.total_costs || 0),
      ganancia_neta: Number(tp.net_profit || 0),
      creado: tp.created_at,
    }));
    await exportToXLSX(`reporte_${dateRange}_${new Date().toISOString().slice(0,10)}`, [
      { name: 'Métricas', data: metricsRow },
      { name: 'Por Sucursal', data: branchData },
      { name: 'Top Clientes', data: topClients },
      { name: 'Top Proveedores', data: topSuppliers },
      { name: 'Transacciones', data: txRows },
      { name: 'Profits', data: profitsRows },
    ]);
  };
  
  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header con controles */}
      <div className="bg-white dark:bg-gray-800 rounded-lg  border border-gray-200 dark:border-gray-700 p-4 mb-6 neon-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
            Reportes y Análisis
          </h1>
          
          <div className="flex flex-wrap items-center gap-3">
            {/* Selector de tipo de reporte */}
            <select 
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="general">Reporte General</option>
              <option value="ventas">Análisis de Ventas</option>
              <option value="compras">Análisis de Compras</option>
              <option value="rentabilidad">Rentabilidad</option>
            </select>
            
            {/* Filtro de sucursal */}
            <select 
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Todas las Sucursales</option>
              {branches.map(branch => (
                <option key={branch.id} value={branch.id}>{branch.name}</option>
              ))}
            </select>
            
            {/* Selector de período */}
            <select 
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="today">Hoy</option>
              <option value="week">Última Semana</option>
              <option value="month">Último Mes</option>
              <option value="quarter">Último Trimestre</option>
              <option value="year">Último Año</option>
            </select>
            
            {/* Export */}
            <div className="relative">
              <button 
                onClick={(e) => {
                  const el = e.currentTarget.nextSibling;
                  if (el) el.classList.toggle('hidden');
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
              >
                <Download className="w-4 h-4" />
                Exportar
              </button>
              <div className="absolute right-0 mt-2 min-w-[160px] rounded-lg border border-gray-200 bg-white p-1 dark:border-gray-700 dark:bg-gray-800 hidden">
                <button onClick={exportCSV} className="w-full text-left rounded-md px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700">CSV</button>
                <button onClick={exportXLSX} className="w-full text-left rounded-md px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700">Excel (.xlsx)</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tarjetas de métricas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700 neon-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">Ingresos Totales</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                ${metrics.totalRevenue.toLocaleString('es-ES')}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {metrics.totalSales} ventas
              </p>
            </div>
            <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-lg">
              <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700 neon-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">Ganancia Neta</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                ${metrics.totalProfit.toLocaleString('es-ES')}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {metrics.profitMargin.toFixed(1)}% margen
              </p>
            </div>
            <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg">
              <TrendingUp className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700 neon-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">Costos Totales</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                ${metrics.totalCosts.toLocaleString('es-ES')}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {metrics.totalOrders} pedidos
              </p>
            </div>
            <div className="bg-orange-100 dark:bg-orange-900/30 p-3 rounded-lg">
              <Package className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700 neon-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">Ticket Promedio</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                ${metrics.averageTicket.toLocaleString('es-ES')}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Por venta
              </p>
            </div>
            <div className="bg-purple-100 dark:bg-purple-900/30 p-3 rounded-lg">
              <BarChart3 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>
      {monipaySummary && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700 neon-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">MoniPay (Ganancia Acumulada)</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                ${metrics.monipayBalance.toLocaleString('es-ES')}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Total histórico de comisiones
              </p>
            </div>
            <div className="bg-emerald-100 dark:bg-emerald-900/30 p-3 rounded-lg">
              <PieChart className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>
        </div>
      )}
      </div>

    {monipaySummary && (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700 neon-border mb-6">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
          <PieChart className="w-5 h-5 mr-2 text-emerald-600 dark:text-emerald-400" />
          Centro de Ganancias MONIPAY
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-300">Ingresos acumulados</p>
            <p className="text-xl font-bold text-emerald-700 dark:text-emerald-300 mt-1">
              ${Number(monipaySummary.total_revenue || 0).toLocaleString('es-ES')}
            </p>
          </div>
          <div className="p-4 bg-rose-50 dark:bg-rose-900/30 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-300">Costos asociados</p>
            <p className="text-xl font-bold text-rose-600 dark:text-rose-300 mt-1">
              ${Number(monipaySummary.total_costs || 0).toLocaleString('es-ES')}
            </p>
          </div>
          <div className="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-300">Ganancia neta</p>
            <p className="text-xl font-bold text-blue-700 dark:text-blue-300 mt-1">
              ${Number(monipaySummary.net_profit || 0).toLocaleString('es-ES')}
            </p>
          </div>
        </div>
      </div>
    )}

      {/* Gráficos y tablas según el tipo de reporte */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Rendimiento por Sucursal */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700 neon-border">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
            <BarChart3 className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
            Rendimiento por Sucursal
          </h2>
          <div className="space-y-4">
            {branchData.map(branch => {
              const maxRevenue = Math.max(...branchData.map(b => b.revenue));
              const percentage = maxRevenue > 0 ? (branch.revenue / maxRevenue) * 100 : 0;
              
              return (
                <div key={branch.id}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {branch.name}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      ${branch.revenue.toLocaleString('es-ES')}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {branch.salesCount} ventas
                    </span>
                    <span className="text-xs text-green-600 dark:text-green-400">
                      Ganancia: ${branch.profit.toLocaleString('es-ES')}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Clientes */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-green-600 dark:text-green-400" />
            Top 10 Clientes
          </h2>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {topClients.map((client, index) => (
              <div key={client.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center">
                  <span className="text-lg font-bold text-gray-400 dark:text-gray-500 mr-3">
                    {index + 1}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {client.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {client.salesCount} compras
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    ${client.totalAmount.toLocaleString('es-ES')}
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-400">
                    +${client.profit.toLocaleString('es-ES')}
                  </p>
                </div>
              </div>
            ))}
            {topClients.length === 0 && (
              <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                No hay datos de clientes en este período
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Tabla de transacciones recientes */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700 neon-border">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
          <FileText className="w-5 h-5 mr-2 text-purple-600 dark:text-purple-400" />
          Transacciones Recientes
        </h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Fecha</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Tipo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Cliente/Proveedor</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Monto</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Ganancia</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Sucursal</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredTransactions.slice(0, 20).map(transaction => {
                const type = transaction.type || transaction.transaction_type;
                const isSale = type === 'sale';
                const client = clients.find(c => String(c.id) === String(transaction.clientId || transaction.client_id));
                const supplier = suppliers.find(s => String(s.id) === String(transaction.supplierId || transaction.supplier_id));
                const branch = branches.find(b => String(b.id) === String(transaction.branchId || transaction.branch_id));
                
                const profitData = transactionProfits.find(tp => tp.transaction_id === transaction.id);
                const profit = profitData ? Number(profitData.net_profit || 0) : 0;
                
                return (
                  <tr key={transaction.id} className="hover:bg-gray-50 hover:dark:bg-gray-700/60">
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {new Date(transaction.date || transaction.created_at).toLocaleDateString('es-ES')}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        isSale 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                      }`}>
                        {isSale ? 'Venta' : 'Compra'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {isSale 
                        ? (client?.name || client?.company_name || 'Sin cliente')
                        : (supplier?.name || 'Sin proveedor')
                      }
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-medium">
                      ${Number(transaction.amount || 0).toLocaleString('es-ES')}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`font-medium ${
                        profit >= 0 
                          ? 'text-green-600 dark:text-green-400' 
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {profit >= 0 ? '+' : ''}${profit.toLocaleString('es-ES')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {branch?.name || 'N/A'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        transaction.status === 'delivered' || transaction.status === 'completed'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : transaction.status === 'cancelled'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                      }`}>
                        {transaction.status === 'pending_deposit' ? 'Esperando Depósito' :
                         transaction.status === 'pending_delivery' ? 'Por Entregar' :
                         transaction.status === 'delivered' ? 'Entregado' :
                         transaction.status === 'completed' ? 'Completado' :
                         transaction.status === 'cancelled' ? 'Cancelado' : 'Pendiente'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredTransactions.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No hay transacciones en el período seleccionado
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
